import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface ProductImageSliderProps {
  images: string[];
  name: string;
  brand?: string;
  productClass?: string;
  tags?: string[];
  className?: string;
  aspectRatio?: string;
  padding?: string;
  badge?: {
    text: string;
    color?: string;
  } | null;
  newBadge?: boolean;
  currentImageIndex?: number;
  onImageIndexChange?: (index: number) => void;
}

export function ProductImageSlider({ 
  images, 
  name,
  brand,
  productClass,
  tags,
  className = '',
  aspectRatio = 'aspect-square',
  padding = 'p-4',
  badge = null,
  newBadge = false,
  currentImageIndex = 0,
  onImageIndexChange
}: ProductImageSliderProps) {
  const [internalImageIndex, setInternalImageIndex] = useState(currentImageIndex);

  // Use external control if provided, otherwise use internal state
  const imageIndex = onImageIndexChange ? currentImageIndex : internalImageIndex;
  const setImageIndex = onImageIndexChange || setInternalImageIndex;

  // Reset image index when images change
  useEffect(() => {
    if (imageIndex >= images.length) {
      setImageIndex(0);
    }
  }, [images, imageIndex, setImageIndex]);

  // Sync with external currentImageIndex prop
  useEffect(() => {
    if (onImageIndexChange && currentImageIndex !== imageIndex) {
      setInternalImageIndex(currentImageIndex);
    }
  }, [currentImageIndex, imageIndex, onImageIndexChange]);

  if (!images || images.length === 0) {
    return (
      <div className={`${aspectRatio} bg-gray-100 flex items-center justify-center ${className}`}>
        <div className="text-gray-400">No image</div>
      </div>
    );
  }

  const nextImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const newIndex = imageIndex === images.length - 1 ? 0 : imageIndex + 1;
    setImageIndex(newIndex);
  };

  const prevImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const newIndex = imageIndex === 0 ? images.length - 1 : imageIndex - 1;
    setImageIndex(newIndex);
  };

  // Generate enhanced alt text for better SEO and accessibility
  const generateAltText = () => {
    let altText = name || 'Toode';
    
    // Add brand if available
    if (brand) {
      altText += ` - ${brand}`;
    }
    
    // Add product type based on class
    if (productClass) {
      switch (productClass) {
        case 'complete-scooters':
          altText += ' trikitõukeratas';
          break;
        case 'decks':
          altText += ' tõuksi tald';
          break;
        case 'bars':
          altText += ' tõuksi lenks';
          break;
        default:
          altText += ' tõuksi detail';
      }
    }
    
    // Add relevant tags for SEO
    if (tags && tags.length > 0) {
      const relevantTags = tags.filter(tag => 
        tag.toLowerCase().includes('pro') ||
        tag.toLowerCase().includes('tase') ||
        tag.toLowerCase().includes('laste') ||
        tag.toLowerCase().includes('algaja') ||
        tag.toLowerCase().includes('kerge') ||
        tag.toLowerCase().includes('park') ||
        tag.toLowerCase().includes('street')
      );
      
      if (relevantTags.length > 0) {
        altText += ` - ${relevantTags.join(', ').toLowerCase()}`;
      }
    }
    
    // Add general SEO terms
    altText += ' - pro trikiratas, sobib lastele ja professionaalidele';
    
    return altText;
  };

  return (
    <div className={`relative ${aspectRatio} ${className} group`}>
      {badge && (
        <div className="absolute top-2 left-2 bg-[#4FC1E9] text-white text-sm px-2 py-1 rounded z-20">
          {badge.text}
        </div>
      )}
      
      {newBadge && (
        <div className="absolute top-2 right-2 bg-green-500 text-white text-sm px-2 py-1 rounded z-20">
          UUS
        </div>
      )}
      
      {images.length > 1 && (
        <>
          <button 
            onClick={prevImage}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-white text-gray-700 rounded-full p-2 shadow-md ring-1 ring-black/5 hover:bg-[#4FC1E9] hover:text-white hover:scale-110 opacity-0 group-hover:opacity-100 transition-all duration-200 z-10"
            aria-label="Eelmine pilt"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          
          <button 
            onClick={nextImage}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-white text-gray-700 rounded-full p-2 shadow-md ring-1 ring-black/5 hover:bg-[#4FC1E9] hover:text-white hover:scale-110 opacity-0 group-hover:opacity-100 transition-all duration-200 z-10"
            aria-label="Järgmine pilt"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </>
      )}
      
      <img
        src={images[imageIndex]}
        alt={generateAltText()}
        title={generateAltText()}
        className={`w-full h-full object-contain ${padding}`}
        onError={(e) => {
          const target = e.target as HTMLImageElement;
          if (!target.src.includes('https://res.cloudinary.com/daka2lob2/image/upload/v1751272740/placeholder-image_d7qyxq.jpg')) {
            target.src = 'https://res.cloudinary.com/daka2lob2/image/upload/v1751272740/placeholder-image_d7qyxq.jpg';
          }
        }}
      />
    </div>
  );
}
